import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { mountShell } from '../components/Shell.js';
import { flashToast } from '../util/toast.js';
import { showLootChanceDialog } from '../components/LootChanceDialog.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatBigNum, formatCrowns } from '../util/formatBig.js';
import {
  CRIME_CATEGORY_LABELS,
  CRIME_OUTCOME_LABELS,
  type CareerStatsDto,
  type CategoryMasteryDto,
  type CrimeCategory,
  type CrimeListing,
} from '@rex-mortis/shared';
import { masteryChipHtml } from '../util/masteryChip.js';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

const CATEGORY_ORDER: CrimeCategory[] = [
  'banditry',
  'smuggling',
  'forgery',
  'poisoning',
  'conspiracy',
  'brigandage',
  'high_schemes',
];

/** Mirrors balance.ts CRIME.resolveGrindCap — the most Resolve-max the
 *  crime-grind ladder can ever grant. Display-only. */
const RESOLVE_GRIND_CAP = 200;

export async function renderMisdeeds(root: HTMLElement): Promise<void> {
  // Refresh state on entry.
  let listings: CrimeListing[] = [];
  let mastery: Record<string, CategoryMasteryDto> = {};
  let careerStats: CareerStatsDto | null = null;
  // Resolve-cost reduction from the Cunning Writs perk (0 if not owned).
  let resolveDiscountPct = 0;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const idx = await api.listCrimes();
    listings = idx.crimes;
    mastery = idx.mastery ?? {};
    resolveDiscountPct = idx.resolveDiscountPct ?? 0;
    // Career ledger — tolerate failure, the panel just hides.
    try {
      careerStats = (await api.getCareerStats()).stats;
    } catch {
      careerStats = null;
    }
  } catch {
    // tolerated
  }

  let fatigueTimer: number | null = null;
  // Preserve which accordions the user opened — across rebuilds AND across
  // visits (persisted to localStorage). `null` means we've never seen a choice,
  // so we seed the first populated category on the very first load. Once the
  // player toggles anything, their exact open set is remembered and restored
  // when they return, instead of snapping back to the first variety.
  // Scoped per account so a shared browser (or a fresh character registered
  // on a machine that played before) doesn't inherit another player's open
  // accordion set — a newcomer must land on the first (gentlest) variety.
  const OPEN_CATS_KEY = `rexmortis.misdeeds.openCats.${store.get().account?.id ?? 'anon'}`;
  const loadOpenCats = (): Set<string> | null => {
    try {
      const raw = localStorage.getItem(OPEN_CATS_KEY);
      if (raw === null) return null;
      const arr = JSON.parse(raw) as string[];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return null;
    }
  };
  const saveOpenCats = (set: Set<string>): void => {
    try {
      localStorage.setItem(OPEN_CATS_KEY, JSON.stringify([...set]));
    } catch {
      /* storage unavailable — non-fatal */
    }
  };
  let openCats: Set<string> | null = loadOpenCats();

  mountShell(root, (main) => {
    // ── Live-update helpers ───────────────────────────────────────────────────
    // An attempt updates the page IN PLACE (resource readout, ledger, the acted
    // card, sibling affordability) instead of rebuilding the whole page. A full
    // rebuild collapsed accordions, re-mounted the chain panel async, and made
    // the viewport jump — so attempts no longer call renderMain.
    const resourceHtml = (char: NonNullable<ReturnType<typeof store.get>['character']>): string => `
      <div class="panel">
        <dl class="kv">
          <dt>Resolve</dt><dd>${char.resolve} / ${char.resolveMax}</dd>
          <dt>Bonus Resolve from Misdeeds</dt><dd>+${char.crimeResolveBonus} / +${RESOLVE_GRIND_CAP}</dd>
          <dt>Cunning</dt><dd>${char.cunning}</dd>
          <dt>Infamy</dt><dd>${char.infamy}</dd>
          <dt>Status</dt><dd>${char.status}${char.statusUntil ? ' until ' + new Date(char.statusUntil).toLocaleTimeString() : ''}</dd>
        </dl>
        ${
          char.status !== 'OKAY'
            ? `<p class="alert danger" style="margin-top:12px">You cannot commit a misdeed while ${statusPhrase(char.status)}.</p>`
            : ''
        }
      </div>`;

    /** Build one fully-wired crime card as a fresh node (so a re-render swaps the
     *  node and its listeners go with it — no duplicate handlers). */
    const buildCard = (crime: CrimeListing): HTMLElement => {
      const char = store.get().character!;
      const card = document.createElement('div');
      card.className = 'crime-card';
      card.dataset.cardKey = crime.key;
      card.innerHTML = cardInnerHtml(crime, char, resolveDiscountPct);
      wireCard(card, crime);
      return card;
    };

    /** Re-render the resource readout, ledger, and every visible card in place,
     *  and refresh the fatigue countdowns. No structural rebuild, no scroll. */
    const applyLiveUpdate = (): void => {
      const char = store.get().character;
      if (!char) return;
      const rHost = main.querySelector<HTMLElement>('#resource-host');
      if (rHost) rHost.innerHTML = resourceHtml(char);
      const lHost = main.querySelector<HTMLElement>('#ledger-host');
      if (lHost) lHost.innerHTML = crimeLedgerHtml(careerStats);
      main.querySelectorAll<HTMLElement>('[data-card-key]').forEach((el) => {
        const crime = listings.find((c) => c.key === el.dataset.cardKey);
        if (crime) el.replaceWith(buildCard(crime));
      });
      setupFatigueCountdowns();
    };

    /** Wire one card's attempt button + loot popup. Closes over applyLiveUpdate. */
    function wireCard(card: HTMLElement, crime: CrimeListing): void {
      const lootRow = card.querySelector<HTMLElement>('[data-loot]');
      if (lootRow) {
        const open = (ev: Event): void => {
          ev.preventDefault();
          ev.stopPropagation();
          showLootChanceDialog({
            title: crime.name,
            dropChance: crime.dropChance,
            dropTable: crime.dropTable,
            materials: {
              poolTier: crime.materialPoolTier,
              dropChance: crime.materialDropChance,
              dropTable: crime.materialDropTable,
            },
          });
        };
        lootRow.addEventListener('click', open);
        lootRow.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') open(e);
        });
      }

      const btn = card.querySelector<HTMLButtonElement>('button[data-attempt]');
      btn?.addEventListener('click', async () => {
        const key = btn.dataset.attempt!;
        const stakeNum = Number(btn.dataset.stake ?? '0');
        if (stakeNum > 0) {
          const { showConfirmDialog } = await import('../components/ConfirmDialog.js');
          const ok = await showConfirmDialog({
            title: btn.dataset.name ?? 'Venture the stake?',
            message: `${formatCrowns(stakeNum)} crowns go on the table before the job. A Triumph returns them with the profit, a Partial returns them alone — anything worse and the stake is gone.`,
            confirmLabel: 'Venture it',
          });
          if (!ok) return;
        }
        // Lock every attempt button on the page while this one resolves.
        main.querySelectorAll<HTMLButtonElement>('button[data-attempt]').forEach((b) => (b.disabled = true));
        try {
          const result = await api.attemptCrime({ crimeKey: key });
          store.set({ character: result.character });
          flashToast(buildFlash(result));
          try {
            const idx = await api.listCrimes();
            listings = idx.crimes;
            mastery = idx.mastery ?? {};
            resolveDiscountPct = idx.resolveDiscountPct ?? 0;
          } catch { /* keep stale listing */ }
          try {
            careerStats = (await api.getCareerStats()).stats;
          } catch { /* keep stale stats */ }
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Attempt failed.';
          flashToast({ kind: 'danger', message: msg });
        } finally {
          applyLiveUpdate();
        }
      });
    }

    /** (Re)bind the live fatigue countdowns across the current cards. */
    function setupFatigueCountdowns(): void {
      if (fatigueTimer !== null) {
        window.clearInterval(fatigueTimer);
        fatigueTimer = null;
      }
      const cdEls = Array.from(main.querySelectorAll<HTMLElement>('.crime-fatigue-cd'));
      if (cdEls.length === 0) return;
      let refreshing = false;
      const tick = (): void => {
        let anyExpired = false;
        for (const el of cdEls) {
          const txt = fatigueCountdown(el.dataset.reset);
          if (txt) { el.textContent = txt; } else { anyExpired = true; el.textContent = 'now'; }
        }
        if (anyExpired && !refreshing) {
          refreshing = true;
          if (fatigueTimer !== null) { window.clearInterval(fatigueTimer); fatigueTimer = null; }
          void api.listCrimes().then((idx) => {
            listings = idx.crimes;
            resolveDiscountPct = idx.resolveDiscountPct ?? 0;
            applyLiveUpdate();
          }).catch(() => { /* leave stale */ });
        }
      };
      tick();
      fatigueTimer = window.setInterval(tick, 1000);
    }

    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/misdeeds.webp" alt="" />
        <h1>Misdeeds</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          The straight road is well-watched. The crooked one less so.
        </p>

        <div id="flash"></div>

        <div id="resource-host">${resourceHtml(char)}</div>

        <div id="ledger-host">${crimeLedgerHtml(careerStats)}</div>

        ${char.cunning < 100
          ? `<details class="crime-section" id="chain-fold">
               <summary>The Throne of Smoke — a grand scheme for the truly cunning. Unfold to behold it.</summary>
               <div id="chain-host"></div>
             </details>`
          : `<div id="chain-host"></div>`}

        <div id="cats"></div>
      `;

      // Grand Scheme panel — the multi-stage endgame conspiracy (v1.13C).
      // Self-contained: fetches its own state and re-renders the page header
      // after an attempt so Resolve/crowns/status stay in step.
      {
        const chainHost = main.querySelector<HTMLDivElement>('#chain-host')!;
        // NOTE: the module is deliberately NOT named "ChainPanel" — ad-blocker
        // crypto filter lists block chunk URLs matching *chain*.js, which made
        // this import fail silently for players running them (2026-07-17).
        void import('../components/GreatWorksPanel.js')
          .then(({ mountChainPanel }) => {
            mountChainPanel(chainHost, {
              kind: 'scheme',
              // Light in-place refresh of the header/ledger/cards — the chain panel
              // re-renders its own host, so we must NOT rebuild the whole page.
              onAfterAttempt: () => applyLiveUpdate(),
            });
          })
          .catch(() => {
            // The lazy chunk failed to load — almost always a stale tab after a
            // deploy (its old hashed chunk is gone from the CDN). Show a refresh
            // prompt rather than a silent blank gap where the Grand Scheme sits.
            chainHost.innerHTML = `
              <div class="panel" style="text-align:center;">
                <p style="margin:0 0 var(--s-2) 0;">A newer version of the realm is live — refresh to see the Grand Scheme.</p>
                <button class="btn" id="chain-refresh">Refresh</button>
              </div>`;
            chainHost.querySelector('#chain-refresh')?.addEventListener('click', () => location.reload());
          });
      }

      // One-off styles for the category accordions.
      const style = document.createElement('style');
      style.textContent = `
        /* Each crime variety is its own expandable accordion — mirrors the
           Sparring Hall's collapsible opponent sections. */
        .crime-section {
          margin-bottom: var(--s-3);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          background: var(--c-parchment);
        }
        .crime-section summary {
          cursor: pointer;
          padding: var(--s-2) var(--s-3);
          font-family: var(--font-display);
          color: var(--c-oxblood);
          font-size: 1rem;
          letter-spacing: 0.02em;
          list-style: none;
          user-select: none;
        }
        .crime-section summary::-webkit-details-marker { display: none; }
        .crime-section summary::before { content: '\\25B8\\00A0'; color: var(--c-rule); }
        .crime-section[open] summary::before { content: '\\25BE\\00A0'; }
        .crime-section summary:hover { background: rgba(139, 111, 71, 0.10); }
        .crime-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
          padding: 0 var(--s-3) var(--s-3);
        }
        .crime-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex;
          flex-direction: column;
        }
        /* Per-crime artwork strip across the top of the card. Hidden until the
           matching asset exists in /img/crimes/ (onerror removes it cleanly). */
        .crime-card .crime-art {
          display: block;
          width: calc(100% + 2 * var(--s-3));
          margin: calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) var(--s-2);
          height: 170px;
          object-fit: cover;
          border-bottom: 1px solid var(--c-rule);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        /* High Schemes art was painted with full-figure subjects — the 170px
           strip beheads them. Those cards alone keep the painting's own 3:2
           proportions (capped, slight top bias if a crop kicks in). */
        .crime-card .crime-art--full {
          height: auto;
          aspect-ratio: 3 / 2;
          max-height: 280px;
          object-position: 50% 22%;
        }
        .crime-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .crime-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.92rem;
          margin-bottom: var(--s-2);
        }
        /* Table hugs the bottom of the blurb text; the button is pushed to the
           card's bottom edge so buttons stay aligned across the row. */
        .crime-card .cg { margin-bottom: auto; }
        /* Cost-vs-reward table (.cg) is now shared — styles live in
           styles/pages.css, markup built via util/costReward.ts. */
        .crime-card button { margin-top: auto; }
        /* Career-crime ledger — mirrors the Trades "Thy Ledger" stat grid. */
        .record-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: var(--s-2);
          margin-top: var(--s-2);
        }
        .record-block {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-2) var(--s-3);
        }
        .record-block .label {
          font-family: var(--font-display);
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          letter-spacing: 0.02em;
        }
        .record-block .value {
          font-family: var(--font-numeric);
          font-size: 1.2rem;
          font-weight: 700;
        }
        .record-block.win .value { color: var(--c-success); }
        .record-block.loss .value { color: var(--c-danger); }
      `;
      main.appendChild(style);

      // Render categories as expandable <details> accordions, one per variety.
      const cats = main.querySelector<HTMLDivElement>('#cats')!;
      let catIndex = 0;
      for (const cat of CATEGORY_ORDER) {
        const inCat = listings.filter((c) => c.category === cat);
        if (inCat.length === 0) continue;

        const section = document.createElement('details');
        section.className = 'crime-section';
        section.dataset.cat = cat;
        // First render: open the first populated variety so the page isn't
        // all-collapsed on load. Subsequent renders: honour whatever the user
        // had open at the time of the rebuild.
        if (openCats === null) {
          if (catIndex === 0) section.open = true;
        } else if (openCats.has(cat)) {
          section.open = true;
        }
        catIndex++;

        const summary = document.createElement('summary');
        summary.innerHTML = `${escapeHtml(CRIME_CATEGORY_LABELS[cat])} (${inCat.length})${masteryChipHtml(mastery[cat])}`;
        section.appendChild(summary);

        const grid = document.createElement('div');
        grid.className = 'crime-grid';

        for (const crime of inCat) {
          grid.appendChild(buildCard(crime));
        }
        section.appendChild(grid);
        // Persist the open set the instant the player toggles a variety, so it
        // sticks even if they navigate away without committing a misdeed.
        section.addEventListener('toggle', () => {
          const open = new Set<string>();
          cats.querySelectorAll<HTMLDetailsElement>('details.crime-section').forEach((s) => {
            if (s.open && s.dataset.cat) open.add(s.dataset.cat);
          });
          openCats = open;
          saveOpenCats(open);
        });
        cats.appendChild(section);
      }

      // Cards wire their own loot popup + attempt button (see buildCard); an
      // attempt updates the page in place via applyLiveUpdate, never a rebuild.
      setupFatigueCountdowns();
    };

    anchored(renderMain);
  });

  // Stop the countdown ticking once the player navigates away.
  window.addEventListener(
    'hashchange',
    () => {
      if (fatigueTimer !== null) {
        window.clearInterval(fatigueTimer);
        fatigueTimer = null;
      }
    },
    { once: true },
  );
}

/** Live countdown ("Hh Mm Ss") for fatigue, from an ISO reset time. Empty once
 *  the timer has elapsed. */
function fatigueCountdown(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function buildFlash(r: import('@rex-mortis/shared').AttemptCrimeResponse): FlashState {
  const head = `${CRIME_OUTCOME_LABELS[r.outcome]} — ${r.crimeName}`;
  const tail: string[] = [];

  if (r.outcome === 'TRIUMPH' || r.outcome === 'PARTIAL') {
    if (r.crownsDelta !== '0') tail.push(`+${formatCrowns(r.crownsDelta)} crowns`);
    if (r.cunningDelta > 0) tail.push(`+${r.cunningDelta} cunning`);
    if (r.infamyDelta > 0) tail.push(`+${r.infamyDelta} infamy`);
    if (r.xpDelta > 0) tail.push(`+${formatBigNum(r.xpDelta)} XP`);
    if (r.leveledUp) tail.push('You reached a new level!');
    if (r.itemDropped) {
      tail.push(`Loot: ${r.itemDropped.qty}× ${r.itemDropped.name}`);
    }
    // Diminishing returns nudge — only when this attempt was actually reduced.
    if (r.crownMultiplier < 1) {
      tail.push(`crowns cut to ${Math.round(r.crownMultiplier * 100)}% — vary your misdeeds`);
    }
  } else if (r.outcome === 'FOILED') {
    // Themed mishap line (mirrors Good Deeds); fall back to the old generic line.
    tail.push(r.failureBlurb ?? 'No reward, no penalty. Try again when conditions favour you.');
  } else {
    if (r.failureBlurb) tail.push(r.failureBlurb);
    if (r.cunningDelta < 0) tail.push(`${r.cunningDelta} cunning`);
    if (r.infamyDelta < 0) tail.push(`${r.infamyDelta} infamy`);
    if (r.jailedUntil) {
      tail.push(`Sent to the Dungeon until ${new Date(r.jailedUntil).toLocaleTimeString()}.`);
    }
  }

  const kind: FlashState['kind'] =
    r.outcome === 'TRIUMPH'
      ? 'success'
      : r.outcome === 'PARTIAL'
        ? 'success'
        : r.outcome === 'FOILED'
          ? 'warn'
          : 'danger';

  return { kind, message: `${head}\n${tail.join(' · ')}` };
}

/** The inner HTML of a single crime card. Pure (no DOM/listeners) so it can be
 *  rebuilt in place after an attempt without a whole-page render. */
function cardInnerHtml(
  crime: CrimeListing,
  char: NonNullable<ReturnType<typeof store.get>['character']>,
  resolveDiscountPct: number,
): string {
  const cunningShort =
    char.cunning < crime.cunningRequired
      ? `<span class="cg-v warn">${crime.cunningRequired}+ (you ${formatBigNum(char.cunning)})</span>`
      : `<span class="cg-v">${crime.cunningRequired}+</span>`;
  const artSlug = crime.key.replace(/\./g, '-');
  const cm = crime.crownMultiplier ?? 1;
  const crownLo = Math.floor(crime.crowns[0] * cm);
  const crownHi = Math.floor(crime.crowns[1] * cm);
  const resetIso = crime.repeatResetAt;
  const fatigueRow = cm < 1
    ? `<div class="cg-row"><span class="cg-k">Fatigue</span><span class="cg-v warn">−${Math.round((1 - cm) * 100)}% crowns${resetIso ? ` · clears in <span class="crime-fatigue-cd" data-reset="${escapeHtml(resetIso)}">${fatigueCountdown(resetIso)}</span>` : ''}</span></div>`
    : '';
  const stake = crime.stakeCrowns ?? 0;
  const walletCoversStake = stake === 0 || BigInt(char.crowns) >= BigInt(stake);
  const stakeRow = stake > 0
    ? `<div class="cg-row"><span class="cg-k">Stake</span><span class="cg-v warn" title="Laid down before the attempt: returned with the profit on a Triumph, returned alone on a Partial, forfeited if the scheme fails.">${formatCrowns(stake)} at risk</span></div>`
    : '';
  return `
    <img class="crime-art${crime.category === 'high_schemes' ? ' crime-art--full' : ''}" src="/img/crimes/${artSlug}.webp" alt=""
      onerror="this.remove()" />
    <h3>${escapeHtml(crime.name)}</h3>
    <div class="blurb">${escapeHtml(crime.blurb)}</div>
    <div class="cg">
      <div class="cg-col">
        <div class="cg-head">Requires</div>
        <div class="cg-row"><span class="cg-k">Resolve</span>${renderResolveCost(crime.resolveCost, resolveDiscountPct)}</div>
        <div class="cg-row"><span class="cg-k">Cunning</span>${cunningShort}</div>
        ${stakeRow}
        ${crime.disastrousChance > 0 ? `<div class="cg-row"><span class="cg-k">Dungeon</span><span class="cg-v warn">${Math.round(crime.disastrousChance * 100)}% if disastrous</span></div>` : ''}
      </div>
      <div class="cg-col">
        <div class="cg-head">Reward · on success</div>
        <div class="cg-row"><span class="cg-k">${stake > 0 ? 'Profit' : 'Crowns'}</span><span class="cg-v gain">${formatCrowns(crownLo)}–${formatCrowns(crownHi)}${stake > 0 ? ' + stake back' : ''}</span></div>
        ${fatigueRow}
        <div class="cg-row"><span class="cg-k">Cunning</span><span class="cg-v gain">+${formatBigNum(crime.cunningOnSuccess)}</span></div>
        <div class="cg-row"><span class="cg-k">Infamy</span><span class="cg-v gain">+${formatBigNum(crime.infamyOnSuccess)}</span></div>
        ${crime.canDropItems ? `<div class="cg-row clickable" data-loot="${crime.key}" role="button" tabindex="0" title="See item odds"><span class="cg-k">Loot</span><span class="cg-v gain">${Math.round(crime.dropChance * 100)}% drop ⓘ</span></div>` : ''}
      </div>
    </div>
    <button class="btn" data-attempt="${crime.key}" data-stake="${stake}" data-name="${escapeHtml(crime.name)}"
      ${
        char.status !== 'OKAY' || char.resolve < effResolve(crime.resolveCost, resolveDiscountPct) || !walletCoversStake
          ? 'disabled'
          : ''
      }>
      ${stake > 0 ? (walletCoversStake ? 'Venture the stake' : `Need ${formatCrowns(stake)} in hand`) : 'Attempt'}
    </button>
  `;
}

/**
 * Career-crime ledger panel for the top of the Misdeeds page — mirrors the
 * Trades "Thy Ledger" and Sparring "Thy Record" stat grids. Returns '' when
 * stats aren't loaded so the page still renders.
 */
function crimeLedgerHtml(stats: CareerStatsDto | null): string {
  if (!stats) return '';
  const c = stats.crimes;
  const gotAway = c.triumphs + c.partials;
  const successRate = c.attempts > 0 ? Math.round((gotAway / c.attempts) * 100) : 0;
  const block = (label: string, value: string | number, klass = ''): string =>
    `<div class="record-block ${klass}">
       <div class="label">${label}</div>
       <div class="value">${value}</div>
     </div>`;
  return `
    <section class="panel" id="ledger-panel">
      <h2 style="margin-top:0;">Thy Ledger of Misdeeds</h2>
      <div class="record-grid">
        ${block('Triumphs', formatBigNum(c.triumphs), 'win')}
        ${block('Partials', formatBigNum(c.partials))}
        ${block('Foiled', formatBigNum(c.foiled))}
        ${block('Disastrous', formatBigNum(c.disastrous), 'loss')}
        ${block('Attempts', formatBigNum(c.attempts))}
        ${block('Success rate', `${successRate}%`)}
        ${block('Crowns earned', formatCrowns(c.crownsEarned))}
      </div>
    </section>
  `;
}

/** Effective Resolve cost after the Cunning Writs discount. Mirrors the server's
 *  Math.ceil(cost*(1-pct)) — so a 1-Resolve misdeed stays at 1. */
function effResolve(base: number, pct: number): number {
  if (pct <= 0) return base;
  return Math.max(1, Math.ceil(base * (1 - pct)));
}

/** Render the Resolve cost cell. With an active discount that actually changes
 *  the cost, show the base struck through and the reduced value beside it. */
function renderResolveCost(base: number, pct: number): string {
  const eff = effResolve(base, pct);
  if (eff < base) {
    return `<span class="cg-v gain" title="Cunning Writs: −${Math.round(pct * 100)}% Resolve">`
      + `<s style="opacity:.6;font-weight:400">${base}</s> ${eff}</span>`;
  }
  return `<span class="cg-v">${base}</span>`;
}

